/**
 * 공유받은 패스워드 접근 컴포넌트
 * 공유 링크를 통해 접근 시 로그인 인증 및 권한 확인
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getSharedPasswordByToken, acceptSharedPassword, rejectSharedPassword } from '../../services/sharedPasswordService';
import { Shield, AlertCircle, Clock, User, Mail, Lock, ExternalLink } from 'lucide-react';
import './SharedPasswordAccess.css';

const SharedPasswordAccess = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [sharedPassword, setSharedPassword] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessAttempted, setAccessAttempted] = useState(false);

  useEffect(() => {
    const handleSharedPasswordAccess = async () => {
      if (authLoading) return;
      
      // 로그인되지 않은 경우 로그인 페이지로 리디렉션
      if (!user) {
        const currentUrl = window.location.href;
        const encodedUrl = encodeURIComponent(currentUrl);
        navigate(`/login?redirect=${encodedUrl}`);
        return;
      }

      setAccessAttempted(true);
      
      try {
        // 토큰으로 공유받은 패스워드 조회
        const shared = await getSharedPasswordByToken(user.uid, token);
        
        // 공유받은 사용자와 로그인한 사용자가 일치하는지 확인
        if (shared.sharedTo !== user.uid) {
          setError('이 공유 링크에 접근할 권한이 없습니다.');
          return;
        }
        
        setSharedPassword(shared);
      } catch (err) {
        console.error('공유받은 패스워드 접근 실패:', err);
        setError(err.message || '공유받은 패스워드를 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    handleSharedPasswordAccess();
  }, [token, user, authLoading, navigate]);

  // 공유받은 패스워드 메뉴로 이동
  const handleGoToSharedPasswords = () => {
    navigate('/shared-passwords');
  };

  // 수락 처리
  const handleAccept = async () => {
    try {
      await acceptSharedPassword(user.uid, sharedPassword.id);
      setSharedPassword(prev => ({
        ...prev,
        status: 'accepted',
        acceptedAt: new Date()
      }));
    } catch (err) {
      setError('수락 처리 중 오류가 발생했습니다.');
    }
  };

  // 거절 처리
  const handleReject = async () => {
    try {
      await rejectSharedPassword(user.uid, sharedPassword.id);
      setSharedPassword(prev => ({
        ...prev,
        status: 'rejected',
        rejectedAt: new Date()
      }));
    } catch (err) {
      setError('거절 처리 중 오류가 발생했습니다.');
    }
  };

  // 로딩 상태
  if (loading || authLoading) {
    return (
      <div className="shared-password-access">
        <div className="shared-password-access__loading">
          <div className="loading-spinner"></div>
          <p>공유받은 패스워드를 확인 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="shared-password-access">
        <div className="shared-password-access__error">
          <AlertCircle size={48} className="error-icon" />
          <h2>접근 오류</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button 
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              <User size={16} />
              로그인하기
            </button>
            <button 
              onClick={() => navigate('/')}
              className="btn btn-secondary"
            >
              홈으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!sharedPassword) {
    return (
      <div className="shared-password-access">
        <div className="shared-password-access__not-found">
          <AlertCircle size={48} className="not-found-icon" />
          <h2>공유받은 패스워드를 찾을 수 없습니다</h2>
          <p>링크가 만료되었거나 잘못된 링크입니다.</p>
          <button 
            onClick={() => navigate('/')}
            className="btn btn-primary"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const { passwordData, senderName, senderEmail, status, expiresAt, shareMessage } = sharedPassword;
  const isExpired = sharedPassword.isExpired;
  const timeRemaining = Math.max(0, sharedPassword.timeRemaining);

  return (
    <div className="shared-password-access">
      <div className="shared-password-access__container">
        {/* 헤더 */}
        <div className="shared-password-access__header">
          <Shield size={32} className="header-icon" />
          <h1>공유받은 패스워드</h1>
          <p>안전한 패스워드 공유 시스템</p>
        </div>

        {/* 공유자 정보 */}
        <div className="shared-password-access__sender">
          <div className="sender-info">
            <User size={20} />
            <span className="sender-name">{senderName || '알 수 없음'}</span>
          </div>
          <div className="sender-email">
            <Mail size={16} />
            <span>{senderEmail || '이메일 없음'}</span>
          </div>
        </div>

        {/* 공유 메시지 */}
        {shareMessage && (
          <div className="shared-password-access__message">
            <p>"{shareMessage}"</p>
          </div>
        )}

        {/* 만료 정보 */}
        <div className={`shared-password-access__expiry ${isExpired ? 'expired' : ''}`}>
          <Clock size={16} />
          <span>
            {isExpired ? 
              '만료됨' : 
              `${Math.ceil(timeRemaining / (1000 * 60 * 60))}시간 후 만료`
            }
          </span>
        </div>

        {/* 패스워드 정보 */}
        {passwordData && !isExpired && (
          <div className="shared-password-access__password">
            <div className="password-header">
              <Lock size={20} />
              <h3>패스워드 정보</h3>
            </div>
            <div className="password-details">
              <div className="password-field">
                <label>사이트/서비스</label>
                <span>{passwordData.site || '알 수 없음'}</span>
              </div>
              <div className="password-field">
                <label>사용자명</label>
                <span>{passwordData.username || '알 수 없음'}</span>
              </div>
              <div className="password-field">
                <label>패스워드</label>
                <span className="password-value">{passwordData.password || '알 수 없음'}</span>
              </div>
              {passwordData.url && (
                <div className="password-field">
                  <label>웹사이트</label>
                  <a href={passwordData.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={16} />
                    {passwordData.url}
                  </a>
                </div>
              )}
              {passwordData.notes && (
                <div className="password-field">
                  <label>메모</label>
                  <span>{passwordData.notes}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 액션 버튼 */}
        <div className="shared-password-access__actions">
          {status === 'pending' && !isExpired && (
            <>
              <button 
                onClick={handleAccept}
                className="btn btn-success"
              >
                수락하기
              </button>
              <button 
                onClick={handleReject}
                className="btn btn-danger"
              >
                거절하기
              </button>
            </>
          )}
          
          {status === 'accepted' && (
            <div className="status-message accepted">
              ✅ 이미 수락된 패스워드입니다
            </div>
          )}
          
          {status === 'rejected' && (
            <div className="status-message rejected">
              ❌ 거절된 패스워드입니다
            </div>
          )}
          
          {isExpired && (
            <div className="status-message expired">
              ⏰ 만료된 패스워드입니다
            </div>
          )}
        </div>

        {/* 하단 링크 */}
        <div className="shared-password-access__footer">
          <button 
            onClick={handleGoToSharedPasswords}
            className="btn btn-outline"
          >
            공유받은 패스워드 목록 보기
          </button>
          <button 
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharedPasswordAccess;